import { useState, useEffect, useMemo } from 'react'
import { ActionIcon, Anchor, Badge, Box, Paper, Text, Tooltip } from '@mantine/core'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { vocabularyApi, type WordFlashcard } from '../../api/vocabulary'
import { FlashCard } from '../../components/vocabulary/FlashCard'

type Status = 'known' | 'unknown' | 'familiar' | 'skipped'
const REVIEWABLE_STATUSES = new Set(['unknown', 'familiar'])

export function VocabReview() {
  const navigate = useNavigate()
  const [words, setWords] = useState<WordFlashcard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [results, setResults] = useState<Array<Status | undefined>>([])

  // Pre-compute random front/back for each word (50/50 chance)
  const showBackMap = useMemo(() => words.map(() => Math.random() > 0.5), [words])

  useEffect(() => {
    vocabularyApi.getDailyReview()
      .then((res) => {
        setWords(res.data.filter((word) => word.user_status && REVIEWABLE_STATUSES.has(word.user_status)))
        setLoading(false)
      })
      .catch(() => { setError('Failed to load review words'); setLoading(false) })
  }, [])

  const handleResponse = (status: Status) => {
    const word = words[currentIndex]
    if (status !== 'skipped') {
      vocabularyApi.updateWordStatus(word.id, status, 'review').catch(() => {})
    }
    setResults((prev) => {
      const next = [...prev]
      next[currentIndex] = status
      return next
    })
    setCurrentIndex((prev) => prev + 1)
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }

  const total = words.length
  const done = currentIndex >= total && total > 0
  const progress = total > 0 ? (currentIndex / total) * 100 : 0

  const knownCount = results.filter((r) => r === 'known').length
  const unknownCount = results.filter((r) => r === 'unknown').length
  const familiarCount = results.filter((r) => r === 'familiar').length
  const xp = knownCount * 10 + familiarCount * 5
  const pageStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    minHeight: '80vh',
    background: 'var(--sat-bg)',
    color: 'var(--sat-text)',
    padding: '24px 16px',
  }
  const ctaButtonStyle = {
    background: 'var(--sat-green)',
    color: 'var(--mantine-color-white)',
    border: '1px solid var(--sat-border)',
    borderRadius: 10,
    padding: '12px 32px',
    fontSize: 20.8,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 4px 20px var(--sat-shadow)',
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', background: 'var(--sat-bg)' }}>
        <Text style={{ fontSize: 23.4, color: 'var(--sat-green)' }}>Loading review words...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', background: 'var(--sat-bg)' }}>
        <Text style={{ fontSize: 23.4, color: 'var(--sat-red)' }}>{error}</Text>
      </Box>
    )
  }

  if (total === 0) {
    return (
      <Box style={{ ...pageStyle, justifyContent: 'center', gap: 16 }}>
        <Text style={{ fontSize: 31.2, fontWeight: 600, color: 'var(--sat-green)' }}>
          Congrats! You remember all words.
        </Text>
        <Text style={{ fontSize: 19.5, color: 'var(--sat-text-dim)', textAlign: 'center' }}>
          Want to have a test?{' '}
          <Anchor
            component="button"
            type="button"
            onClick={() => navigate('/vocabulary/test')}
            style={{ fontSize: 19.5, fontWeight: 600 }}
          >
            Go to Vocabulary Test
          </Anchor>
        </Text>
        <button
          onClick={() => navigate('/dashboard')}
          style={ctaButtonStyle}
        >
          Back to Dashboard
        </button>
      </Box>
    )
  }

  if (done) {
    return (
      <Box style={{ ...pageStyle, justifyContent: 'center', gap: 24, padding: 32 }}>
        
        <Text style={{ fontSize: 41.6, fontWeight: 700, color: 'var(--sat-text)' }}>Review Complete!</Text>
        <Box style={{ display: 'flex', gap: 24 }}>
          <StatBox label="Known" value={knownCount} color="var(--sat-green)" />
          <StatBox label="Unknown" value={unknownCount} color="var(--sat-red)" />
          <StatBox label="Familiar" value={familiarCount} color="var(--sat-yellow)" />
        </Box>
        <Text style={{ fontSize: 26, color: 'var(--sat-green)', fontWeight: 600 }}>+{xp} XP earned</Text>
        <button
          onClick={() => navigate('/dashboard')}
          style={ctaButtonStyle}
        >
          Back to Dashboard
        </button>
      </Box>
    )
  }

  const showBack = showBackMap[currentIndex]

  return (
    <Box style={pageStyle}>
      {/* Progress bar */}
      <Box style={{ width: '100%', maxWidth: 400, marginBottom: 24 }}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 16.9, color: 'var(--sat-text)', marginBottom: 8, gap: 12 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip label="Unknown words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-red-soft)', color: 'var(--sat-red)' }}
              >
                {unknownCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Familiar words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-yellow-soft)', color: 'var(--sat-warning-text)' }}
              >
                {familiarCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Known words" withArrow>
              <Badge
                size="lg"
                radius="sm"
                style={{ background: 'var(--sat-green-soft)', color: 'var(--sat-green)' }}
              >
                {knownCount}
              </Badge>
            </Tooltip>
          </Box>
          <span>{currentIndex + 1} / {total}</span>
        </Box>
        <Box style={{ width: '100%', height: 8, background: 'var(--sat-blush)', borderRadius: 999 }}>
          <Box style={{ width: `${progress}%`, height: '100%', background: 'var(--sat-red)', borderRadius: 999, transition: 'width 0.3s ease' }} />
        </Box>
      </Box>

      {/* Card */}
      <Box style={{ position: 'relative', width: 350, height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FlashCard
          key={words[currentIndex].id}
          word={words[currentIndex]}
          onSwipeRight={() => handleResponse('known')}
          onSwipeLeft={() => handleResponse('unknown')}
          onSwipeUp={() => handleResponse('familiar')}
          onSwipeDown={() => handleResponse('skipped')}
          showBackFirst={showBack}
          blankWordInContext={showBack}
        />
      </Box>

      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 350, marginTop: 20 }}>
        <ActionIcon
          size={48}
          radius="xl"
          variant="light"
          color="gray"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          aria-label="Previous word"
        >
          <ChevronLeft size={22} />
        </ActionIcon>
        <ActionIcon
          size={48}
          radius="xl"
          variant="light"
          color="eveningSea"
          onClick={() => handleResponse('skipped')}
          aria-label="Next word"
        >
          <ChevronRight size={22} />
        </ActionIcon>
      </Box>
    </Box>
  )
}

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Paper withBorder style={{ background: 'var(--sat-surface)', borderColor: 'var(--sat-border)', borderRadius: 12, padding: '16px 24px', textAlign: 'center', boxShadow: '0 2px 8px var(--sat-shadow)' }}>
      <Text style={{ fontSize: 36.4, fontWeight: 700, color }}>{value}</Text>
      <Text style={{ fontSize: 16.9, color: 'var(--sat-text-dim)' }}>{label}</Text>
    </Paper>
  )
}
